import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  Divider,
  Button,
  Stack,
  Chip
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BuildIcon from '@mui/icons-material/Build';

function Transparency() {
  const pillars = [
    { icon: <ReceiptLongIcon />, title: 'Clear Pricing', desc: 'No hidden fees or markups. Every quote includes our exact premium and processing fees upfront. Full transparency on collateral ratios.' },
    { icon: <GavelIcon />, title: 'Certified Grading', desc: 'We provide third-party, unbiased GIA and IGJ grading reports for every single asset. All certificates are on-chain verified.' },
    { icon: <VerifiedUserIcon />, title: 'Audit Trails', desc: 'Every transaction is logged with a complete, cryptographically signed audit trail for total accountability. Smart contract events are publicly verifiable.' },
  ];

  return (
    <Box sx={{ backgroundColor: '#050505', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* ============ HERO SECTION ============ */}
      <Box
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: 'url(/images/hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 50%, rgba(0, 0, 0, 0.95) 100%)', 
          zIndex: 1 
        }} />
        
        {/* Slow Zoom */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundImage: 'url(/images/hero.png)', backgroundSize: 'cover', backgroundPosition: 'center', 
          animation: 'slowZoom 25s ease-in-out infinite alternate', 
          '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
          zIndex: 0 
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, pt: { xs: '140px', md: '180px' } }}>
          
          {/* Development Badge */}
          <Box sx={{ display: 'inline-block', mb: 3 }}>
            <Chip
              icon={<BuildIcon sx={{ fontSize: '14px !important' }} />}
              label="On-Chain Transparency • Coming Soon"
              sx={{
                bgcolor: 'rgba(212, 175, 55, 0.12)',
                color: '#d4af37',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: '20px',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                '& .MuiChip-icon': { color: '#d4af37' }
              }}
            />
          </Box>

          <Typography variant="h1" component="h1" sx={{
            fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: '3rem', sm: '3.5rem', md: '5rem' }, lineHeight: 1.1, mb: 4,
            background: 'linear-gradient(90deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)', backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 6s linear infinite',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.8))',
            '@keyframes shimmer': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-200% center' } },
          }}>
            Full Transparency
          </Typography>

          <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 300, maxWidth: '700px', mx: 'auto', mb: 5, lineHeight: 1.8, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            Every transaction is documented with clear pricing, certified grading, and complete on-chain audit trails. 
            We believe in total transparency with our investors.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />} sx={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)', 
              color: '#000000', 
              fontWeight: 800, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px', 
              boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)', 
              transition: 'all 0.5s ease',
              '&:hover': { 
                background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)', 
                color: '#000000', 
                boxShadow: '0 15px 50px rgba(212, 175, 55, 0.5)', 
                transform: 'translateY(-3px)' 
              },
            }}>
              Join Waitlist
            </Button>
            <Button component={RouterLink} to="/technology" variant="outlined" sx={{
              borderColor: '#d4af37', 
              color: '#d4af37', 
              fontWeight: 700, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px',
              '&:hover': { borderColor: '#ffffff', color: '#ffffff', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
            }}>
              View Technology
            </Button>
          </Stack>
        </Container>
      </Box>
      {/* ============ END HERO ============ */}

      {/* ============ MAIN CONTENT ============ */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        
        {/* Pillars - All in one row on all screen sizes */}
        <Grid container spacing={2.5} alignItems="stretch" sx={{ flexWrap: 'nowrap' }}>
          {pillars.map((item, index) => (
            <Grid item xs={4} key={index} sx={{ display: 'flex', minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.15)',
                  borderRadius: '6px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s ease',
                  minHeight: '280px',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                    pointerEvents: 'none',
                  },
                  '&:hover': { 
                    borderColor: '#d4af37', 
                    transform: 'translateY(-4px)', 
                    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.8), 0 0 15px rgba(212, 175, 55, 0.15)',
                    '& .pillar-icon': {
                      borderColor: '#d4af37',
                      color: '#d4af37',
                      backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    }
                  },
                }}
              >
                <Box 
                  className="pillar-icon"
                  sx={{ 
                    color: '#d4af37', 
                    height: '50px', 
                    width: '50px',
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderRadius: '4px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    backgroundColor: 'rgba(212, 175, 55, 0.05)',
                    transition: 'all 0.4s ease',
                    '& svg': { fontSize: '2rem' } 
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="h6" sx={{ 
                  fontFamily: "'Playfair Display', serif", 
                  color: '#fff', 
                  mb: 1.5, 
                  fontSize: '1.1rem' 
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#a1a1a1', 
                  lineHeight: 1.7, 
                  fontSize: '0.85rem',
                  flex: 1
                }}>
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Transparency Commitment */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.15)', mb: 6 }} />
          
          <Typography variant="body2" sx={{ color: '#a1a1a1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <VerifiedUserIcon sx={{ color: '#d4af37', fontSize: '20px' }} />
            100% Of Our Records Will Be Available For Public On-Chain Verification At Launch.
          </Typography>
      
        </Box>

      </Container>
    </Box>
  );
}

export default Transparency;